/* oxlint-disable tailwindcss/no-custom-classname -- `border-border-error`, `bg-surface-error` and `text-text-error` are not semantic tokens and never resolved to anything. Pre-existing: the error state has rendered unstyled since this block was written, and correcting the tokens is a visual change this move does not make. */
import { TypographyCaption } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { AlertCircle, Check, FileText, Loader2 } from 'lucide-react';

import type { JSX } from 'react';

interface InlinePatientSummaryBlockProps {
  status: 'fetching' | 'completed' | 'error';
  error?: string;
  className?: string;
}

/**
 * V2 Inline Patient Summary Block - Shows patient summary (pre-chart) tool activity in the chat timeline (EVI-591)
 *
 * Visual states:
 * - FETCHING: Spinning loader with "Loading patient context..." text
 * - COMPLETED: Check icon with "Patient context loaded" text
 * - ERROR: Error icon with error message
 *
 * Similar to InlineSearchBlock but for patient summary tool events
 */
export const InlinePatientSummaryBlock = ({
  status,
  error,
  className,
}: InlinePatientSummaryBlockProps): JSX.Element => {
  const { t } = useTranslations();

  // Keyframes for animations
  const animationKeyframes = `
    @keyframes icon-fade-in {
      from { opacity: 0; transform: scale(0.8); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes text-shimmer {
      0% { mask-position: 150%; }
      100% { mask-position: -50%; }
    }
  `;

  // Fetching state with spinning loader and shimmer text
  if (status === 'fetching') {
    return (
      <div
        className={cn(
          'relative w-full min-w-0 overflow-hidden rounded-lg border border-border duration-300 animate-in fade-in',
          className
        )}
      >
        <style>{animationKeyframes}</style>
        <div className="flex items-center p-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {/* Spinning loader */}
            <Loader2 className="size-4 shrink-0 animate-spin text-text-secondary" />
            {/* Text with shimmer effect */}
            <span
              className="min-w-0 truncate text-sm text-text-secondary"
              style={{
                maskImage:
                  'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.4) 50%, #000 65%, #000 100%)',
                maskSize: '200% 100%',
                WebkitMaskImage:
                  'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.4) 50%, #000 65%, #000 100%)',
                WebkitMaskSize: '200% 100%',
                animation: 'text-shimmer 2s ease-in-out infinite',
              }}
            >
              {t('chat.loadingPatientContext')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div
        className={cn(
          'border-border-error bg-surface-error w-full min-w-0 overflow-hidden rounded-lg border duration-300 animate-in fade-in',
          className
        )}
      >
        <style>{animationKeyframes}</style>
        <div className="flex items-center gap-2 p-3">
          <AlertCircle
            className="text-text-error size-4 shrink-0"
            style={{ animation: 'icon-fade-in 250ms ease-out forwards' }}
          />
          <span className="text-text-error w-0 flex-1 truncate text-sm">
            {error || t('chat.patientContextError')}
          </span>
        </div>
      </div>
    );
  }

  // Completed state
  return (
    <div
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-lg border border-border duration-300 animate-in fade-in',
        className
      )}
    >
      <style>{animationKeyframes}</style>
      <div className="flex items-center gap-2 p-3">
        <FileText
          className="size-4 shrink-0 text-text-secondary"
          style={{ animation: 'icon-fade-in 250ms ease-out forwards' }}
        />
        <span className="w-0 flex-1 truncate text-sm text-text-secondary">
          {t('chat.patientContextLoaded')}
        </span>
        <TypographyCaption className="flex shrink-0 items-center gap-1 whitespace-nowrap text-text-tertiary duration-200 animate-in fade-in">
          <Check className="size-3.5" />
          {t('chat.completed')}
        </TypographyCaption>
      </div>
    </div>
  );
};
